import { useTranslation } from 'react-i18next'
import { Mail, Phone, MapPin, Linkedin } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { getMediaUrl } from '@/utils/media'

export function Footer() {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const companyLinks = [
    { label: t('nav.about'), href: '/about' },
    { label: t('nav.certifications'), href: '/certifications' },
  ]

  const productLinks = [
    { label: t('nav.cathodicProtection'), href: '/cathodic-protection' },
    { label: t('nav.oxygenEvolution'), href: '/oxygen-evolution' },
    { label: t('nav.electrochlorination'), href: '/electrochlorination' },
    { label: t('nav.hiseochlor'), href: '/hiseochlor' },
    { label: t('nav.chlorineAlkali'), href: '/chlorine-alkali' },
  ]

  const legalLinks = [
    { label: t('footer.privacy'), href: '/privacy' },
    { label: t('footer.terms'), href: '/terms' },
    { label: t('nav.whistleblowing'), href: '/whistleblowing' },
  ]

  return (
    <footer style={{ backgroundColor: '#003b7a' }} className="text-white">
      <div className="container-main">
        {/* Main footer content */}
        <div className="py-12 lg:py-16">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <img
                src={getMediaUrl('logos/chemical-newtech-logo.png')}
                alt="Chemical Newtech S.p.A."
                className="mb-6 h-12 w-auto"
              />
              <p className="mb-6 text-sm text-white/70">
                {t('footer.tagline')}
              </p>
              <div className="space-y-3 text-sm text-white/70">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                  <span>
                    Via Piemonte, 19<br />
                    25031 Capriolo (BS)<br />
                    Italia
                  </span>
                </div>
                <a
                  href="tel:+390307460669"
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 flex-shrink-0 text-accent" />
                  +39 030 7460669
                </a>
                <a
                  href="mailto:info@chemicalnewtech.com"
                  className="flex items-center gap-3 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 flex-shrink-0 text-accent" />
                  info@chemicalnewtech.com
                </a>
              </div>

              {/* Social links */}
              <div className="mt-6 flex gap-3">
                <a
                  href="https://www.linkedin.com/company/chemical-newtech-spa-titanium-anodes/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-accent hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                {t('footer.company')}
              </h3>
              <ul className="space-y-2">
                {companyLinks.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-sm text-white/70 hover:text-accent transition-colors"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                {t('nav.products')}
              </h3>
              <ul className="space-y-2">
                {productLinks.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-sm text-white/70 hover:text-accent transition-colors"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white">
                {t('footer.resources')}
              </h3>
              <ul className="space-y-2">
                {legalLinks.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink
                      to={link.href}
                      className="text-sm text-white/70 hover:text-accent transition-colors"
                    >
                      {link.label}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col items-center justify-between gap-4 text-center text-sm text-white/50 sm:flex-row sm:text-left">
            <p>
              &copy; {currentYear} Chemical Newtech S.p.A. {t('footer.allRightsReserved')}
            </p>
            <p>
              P.IVA: 02946170988
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
